import { CylinderStrip } from '../components/CylinderStrip';
import { Icon } from '../components/Icon';
import { NumberField } from '../components/NumberField';
import { HeroStat, SnapMetric } from '../components/Metric';
import {
  classifyCht,
  classifyPeakState,
  maxCht,
  referenceCylinder,
} from '../lib/readings';
import { useEngineStore } from '../state/engineStore';
import type { PeakState } from '../types/engine';

const PEAK_STATE_LABEL: Record<PeakState, string> = {
  unknown: '—',
  climbing: 'rising',
  'at-peak': 'at peak',
  'peaked-lop': 'LOP',
  'below-peak-rop': 'ROP',
};

export function MonitorScreen() {
  const {
    config,
    readings,
    setCylinderEgt,
    setCylinderCht,
    setPower,
    setFuelFlow,
    setTas,
  } = useEngineStore();

  const hottest = maxCht(readings.cylinders);
  const chtChip =
    hottest === undefined
      ? undefined
      : classifyCht(hottest.cht, config.chtLimits) === 'danger'
        ? ({ kind: 'danger', text: 'Red line' } as const)
        : classifyCht(hottest.cht, config.chtLimits) === 'warn'
          ? ({ kind: 'warn', text: 'Yellow' } as const)
          : ({ kind: 'ok', text: 'Normal' } as const);

  const ref = referenceCylinder(readings.cylinders);
  const peakedCount = readings.cylinders.filter((c) => c.peakedAt !== undefined).length;
  const allPeaked = peakedCount === config.cylinderCount;

  const refCyl = ref && readings.cylinders.find((c) => c.id === ref.id);
  const refDelta =
    refCyl?.egt !== undefined && refCyl.peakEgt !== undefined
      ? refCyl.peakEgt - refCyl.egt
      : undefined;

  return (
    <div className="col gap-lg">
      {hottest && classifyCht(hottest.cht, config.chtLimits) === 'danger' && (
        <div className="banner danger">
          <Icon name="alert" size={18} />
          <div style={{ flex: 1 }}>
            <strong>CHT above red line</strong> — Cyl #{hottest.id} at{' '}
            {Math.round(hottest.cht)} °F. Reduce power, enrich, open cowl flaps.
          </div>
        </div>
      )}

      <div className="grid-4">
        <HeroStat
          label="Max CHT"
          value={hottest ? Math.round(hottest.cht) : '—'}
          unit={hottest ? '°F' : ''}
          chip={chtChip}
        />
        <div className="card">
          <span className="label">Power</span>
          <div style={{ marginTop: 10 }}>
            <NumberField
              value={readings.power}
              onChange={setPower}
              suffix="%"
              min={0}
              max={100}
              placeholder="—"
              ariaLabel="Engine power percent"
            />
          </div>
        </div>
        <div className="card">
          <span className="label">Fuel flow</span>
          <div style={{ marginTop: 10 }}>
            <NumberField
              value={readings.fuelFlow}
              onChange={setFuelFlow}
              suffix="GPH"
              min={0}
              step={0.1}
              placeholder="—"
              ariaLabel="Fuel flow in gallons per hour"
            />
          </div>
        </div>
        <div className="card">
          <span className="label">TAS</span>
          <div style={{ marginTop: 10 }}>
            <NumberField
              value={readings.tas}
              onChange={setTas}
              suffix="kts"
              min={0}
              placeholder="—"
              ariaLabel="True airspeed in knots"
            />
          </div>
        </div>
      </div>

      <div className="card card-lg">
        <div className="row between" style={{ marginBottom: 12 }}>
          <div>
            <div className="card-h">Cylinder readings</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              Enter EGT and CHT from your engine monitor. Peak EGT is tracked per cylinder.
            </div>
          </div>
          <div
            className="row gap-sm"
            style={{ fontSize: 12, color: 'var(--ink-3)' }}
          >
            <span>
              Peaked: {peakedCount} / {config.cylinderCount}
            </span>
          </div>
        </div>

        <div className="cyl-table">
          <div className="cyl-table-head">
            <div className="stat-label">Cyl</div>
            <div className="stat-label">EGT (°F)</div>
            <div className="stat-label">CHT (°F)</div>
            <div className="stat-label" style={{ textAlign: 'right' }}>
              Peak EGT
            </div>
            <div className="stat-label" style={{ textAlign: 'right' }}>
              State
            </div>
          </div>
          {readings.cylinders.map((c) => {
            const state = classifyPeakState(c);
            const chtState = classifyCht(c.cht, config.chtLimits);
            return (
              <CylinderRow
                key={c.id}
                cylId={c.id}
                egt={c.egt}
                cht={c.cht}
                peakEgt={c.peakEgt}
                state={state}
                chtState={chtState}
                onEgtChange={(v) => setCylinderEgt(c.id, v)}
                onChtChange={(v) => setCylinderCht(c.id, v)}
              />
            );
          })}
        </div>
      </div>

      <div className="grid-2">
        <div className="card card-lg">
          <div className="card-h" style={{ marginBottom: 4 }}>
            Leaning status
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
            Derived from the peaks you have captured this session.
          </div>

          {ref && refCyl ? (
            <div className="col gap-sm">
              <SnapMetric
                label="Reference cylinder (last to peak)"
                value={`#${ref.id}`}
                unit={ref.peakEgt !== undefined ? ` · peak ${Math.round(ref.peakEgt)} °F` : ''}
              />
              {refDelta !== undefined && (
                <SnapMetric
                  label={refDelta <= 3 ? 'Near peak' : 'Δ below reference peak'}
                  value={refDelta <= 3 ? 'at peak' : `${Math.round(refDelta)} °F LOP`}
                  kind={refDelta <= 3 ? 'warn' : 'ok'}
                />
              )}
              <SnapMetric
                label="Cylinders peaked"
                value={`${peakedCount} / ${config.cylinderCount}`}
                kind={allPeaked ? 'ok' : 'neutral'}
              />
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Enter EGTs as you lean. The first time any cylinder's EGT drops more than a
              few degrees below its highest entered value, it is marked "peaked".
            </div>
          )}
        </div>

        <div className="card card-lg">
          <div className="card-h" style={{ marginBottom: 16 }}>
            CHT bars
          </div>
          <CylinderStrip
            cyls={readings.cylinders}
            metric="CHT"
            chtLimits={config.chtLimits}
          />
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: 'var(--ink-3)',
              lineHeight: 1.55,
            }}
          >
            Green &lt; {config.chtLimits.ok} °F · Yellow {config.chtLimits.ok}–
            {config.chtLimits.warn} °F · Red ≥ {config.chtLimits.warn} °F. Adjust in{' '}
            <em>Settings</em>.
          </div>
        </div>
      </div>
    </div>
  );
}

type CylinderRowProps = {
  cylId: number;
  egt: number | undefined;
  cht: number | undefined;
  peakEgt: number | undefined;
  state: PeakState;
  chtState: ReturnType<typeof classifyCht>;
  onEgtChange: (v: number | undefined) => void;
  onChtChange: (v: number | undefined) => void;
};

function CylinderRow({
  cylId,
  egt,
  cht,
  peakEgt,
  state,
  chtState,
  onEgtChange,
  onChtChange,
}: CylinderRowProps) {
  const chtColor =
    chtState === 'danger'
      ? 'var(--danger)'
      : chtState === 'warn'
        ? 'var(--warn)'
        : chtState === 'ok'
          ? 'var(--ok)'
          : 'var(--ink-3)';
  const stateLabel = PEAK_STATE_LABEL[state];
  const stateColor =
    state === 'peaked-lop'
      ? 'var(--ok)'
      : state === 'at-peak'
        ? 'var(--warn)'
        : state === 'climbing'
          ? 'var(--ink-2)'
          : 'var(--ink-3)';
  return (
    <div className="cyl-table-row">
      <div
        className="cyl-id"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--ink-3)',
        }}
      >
        #{cylId}
      </div>
      <div className="cyl-egt">
        <NumberField
          value={egt}
          onChange={onEgtChange}
          min={600}
          max={1800}
          placeholder="EGT"
          ariaLabel={`Cylinder ${cylId} EGT`}
        />
      </div>
      <div className="cyl-cht">
        <NumberField
          value={cht}
          onChange={onChtChange}
          min={100}
          max={500}
          placeholder="CHT"
          ariaLabel={`Cylinder ${cylId} CHT`}
        />
      </div>
      <div
        className="cyl-peak"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          textAlign: 'right',
          color: 'var(--ink-2)',
        }}
      >
        {peakEgt !== undefined ? `${Math.round(peakEgt)} °F` : '—'}
      </div>
      <div
        className="cyl-state"
        style={{
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'right',
          color: chtState === 'unknown' ? stateColor : chtColor,
        }}
      >
        {stateLabel}
      </div>
    </div>
  );
}
